<template>
  <FormItem
    :id="id || name"
    :label="label"
    :label-width="labelWidth"
    :required="isRequired"
    :layout="layout"
    :error-message="errorMessage"
    :help-text="helpText"
    :no-margin="noMargin || isNested"
  >
    <template #before v-if="$slots.before">
      <slot name="before"></slot>
    </template>

    <div class="textarea-wrapper" :class="{ 'is-invalid': errorMessage, 'is-disabled': disabled }">
      <div class="textarea-inner-wrapper">
        <slot name="prefix" v-if="$slots.prefix" class="prefix-content"></slot>

        <textarea
          :id="id || name"
          :type="type"
          :value="modelValue"
          class="form-control h-auto"
          :class="{
            'is-invalid': errorMessage,
            'form-control-sm': size === 'sm',
            'form-control-lg': size === 'lg',
            'with-prefix': $slots.prefix,
            'with-suffix': $slots.suffix || (clearable && modelValue),
            ...$attrs.class
          }"
          :style="{
            width,
            resize: 'none',
            ...$attrs.style
          }"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
          :minlength="minlength"
          :rows="rows"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          @input="handleInput"
          @blur="handleBlur"
          @focus="$emit('focus', $event)"
        ></textarea>

        <div class="suffix-container" v-if="$slots.suffix || (clearable && modelValue)">
          <div v-if="clearable && modelValue" class="clear-btn" @click="clearInput">
            <SvgIcon iconName="icon-icon_universe_close_fill" class="me-1"></SvgIcon>
          </div>
          <slot name="suffix" v-if="$slots.suffix"></slot>
        </div>
      </div>

      <div v-if="maxlength" class="textarea-counter">
        <span>{{ modelValue.length }} / {{ maxlength }}</span>
      </div>
    </div>

    <template #after v-if="$slots.after">
      <slot name="after"></slot>
    </template>
  </FormItem>
</template>

<script setup>
import { computed, inject, onMounted, onBeforeUnmount, ref, provide } from 'vue'
import FormItem from './FormItem.vue'
import SvgIcon from '../SvgIcon.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  name: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'text'
  },
  label: {
    type: String,
    default: ''
  },
  labelWidth: {
    type: String,
    default: undefined
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  helpText: {
    type: String,
    default: ''
  },
  maxlength: {
    type: [String, Number],
    default: undefined
  },
  minlength: {
    type: [String, Number],
    default: undefined
  },
  rows: {
    type: [String, Number],
    default: 3
  },
  autocomplete: {
    type: String,
    default: 'off'
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  layout: {
    type: String,
    default: undefined
  },
  size: {
    type: String,
    default: '',
    validator: value => ['sm', 'lg', ''].includes(value)
  },
  validateOnBlur: {
    type: Boolean,
    default: true
  },
  noMargin: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change', 'clear'])

const formContext = inject('formContext', null)

const isNested = inject('isFormItemNested', false)

provide('isFormItemNested', true)

const layout = computed(() => props.layout || (formContext ? formContext.layout : 'vertical'))
const labelWidth = computed(
  () => props.labelWidth || (formContext ? formContext.labelWidth : undefined)
)

const isRequired = computed(() => {
  if (!formContext || !formContext.rules || !formContext.rules[props.name]) return false

  const rules = formContext.rules[props.name]
  return rules.some(rule => rule.required)
})

const errorMessage = computed(() => {
  if (!formContext || !formContext.errors) return ''
  return formContext.errors[props.name] || ''
})

const handleInput = event => {
  const value = event.target.value
  emit('update:modelValue', value)
  emit('change', value)
}

const handleBlur = event => {
  emit('blur', event)

  if (props.validateOnBlur && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
}

const clearInput = () => {
  emit('update:modelValue', '')
  emit('clear')
}

onMounted(() => {
  if (formContext && formContext.registerFormItem) {
    formContext.registerFormItem(props.name, {
      validate: () => formContext.validateField(props.name)
    })
  }
})

onBeforeUnmount(() => {
  if (formContext && formContext.unregisterFormItem) {
    formContext.unregisterFormItem(props.name)
  }
})
</script>

<style scoped>
.textarea-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  border: 1px solid #D8E8FF;
  border-radius: 0.375rem;
  background-color: #f5faff;
  overflow: hidden;
}

.textarea-wrapper:focus-within {
  border-color: #0068b0;
}

.textarea-wrapper.is-invalid {
  border-color: #c62741 !important;
}

.textarea-wrapper.is-disabled {
  background-color: #eceff2;
}

.textarea-inner-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.prefix-content {
  position: absolute;
  left: 0.75rem;
  top: 0.5rem;
  z-index: 2;
}

textarea.form-control {
  background-color: transparent !important;
  font-size: 0.875rem;
  color: #343b46;
  width: 100%;
  resize: none;
  min-height: 80px;
  padding: 0.5rem 0.75rem;
  border: none !important;
  box-shadow: none !important;
}

textarea.form-control[disabled] {
  background-color: transparent !important;
}

textarea.form-control.with-prefix {
  padding-left: 2.5rem;
}

textarea.form-control.with-suffix {
  padding-right: 2.5rem;
}

textarea.form-control::placeholder {
  color: #8697aa;
}

.suffix-container {
  position: absolute;
  right: 0.75rem;
  top: 0.5rem;
  display: inline-flex;
  flex-shrink: 0;
  text-align: center;
  align-items: center;
  justify-content: center;
  z-index: 2;
  gap: 0.5rem;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.textarea-wrapper:hover .clear-btn {
  opacity: 1;
}

.textarea-counter {
  flex-shrink: 0;
  padding: 0 0.75rem 0.375rem;
  text-align: right;
  color: #8697aa;
  font-size: 0.75rem;
  line-height: 1.2;
}

.clear-btn:hover svg {
  fill: #0068b0;
}
</style>
